import { UseQueryResult, useQuery } from '@tanstack/react-query'

import { AdminListResponse } from '@engine/models/admin'

import { adminFetch, type FetchResourceParams } from '#api/client'
import { retryUnlessClientError, sanitizeParams } from '#api/query'

export interface UseAdminResourceOptions {
  autoRefreshMs?: number
  enabled?: boolean
}

export interface UseAdminResourceResult<T> {
  items: T[]
  isLoading: boolean
  error?: string
  nextCursor?: string
  prevCursor?: string
  total?: number
  lastUpdated?: Date
  refresh: () => Promise<void>
}

export function usePagedFetch<T>(
  resource: string,
  params: FetchResourceParams,
  options: UseAdminResourceOptions = {},
): UseAdminResourceResult<T> {
  const { autoRefreshMs, enabled = true } = options
  const sanitizedParams = sanitizeParams(params)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [resource, JSON.stringify(sanitizedParams)],
    queryFn: async ({ signal }) => {
      const response = await adminFetch<T>(resource, {
        params: sanitizedParams,
        signal,
      })
      return response
    },
    enabled,
    refetchInterval: autoRefreshMs,
  })

  const response = data as AdminListResponse<T> | undefined

  return {
    items: response?.items ?? [],
    isLoading,
    error: error?.message,
    nextCursor: response?.nextCursor,
    prevCursor: response?.prevCursor,
    total: response?.total,
    lastUpdated: data ? new Date() : undefined,
    refresh: () => refetch().then(() => {}),
  }
}

export function useFetch<T>(
  resource: string,
  params: FetchResourceParams,
  options: UseAdminResourceOptions = {},
): UseQueryResult<T> {
  const { autoRefreshMs, enabled = true } = options
  const sanitizedParams = sanitizeParams(params)

  return useQuery<T>({
    queryKey: [resource, JSON.stringify(sanitizedParams)],
    queryFn: async ({ signal }) => {
      const response = await adminFetch<T>(resource, {
        params: sanitizedParams,
        signal,
      })
      return response
    },
    enabled,
    refetchInterval: autoRefreshMs,
    retry: retryUnlessClientError,
  })
}
